// Performance Dashboard: daily summaries, pacing, breakdowns, peer benchmark.
// Funnel counts come from the generated leads, so the dashboard, the leads
// list and the lead drilldowns all agree.
import {
  DEMO_CLIENT, MARKETS, VERTICALS, addDays, dailyRows, groupBy, iso, monthKey, nextUrl, round2, today,
} from '../client';
import { ZIP_FACTS } from '../zipFacts';
import { allLeads } from './40_leads';
import { budgetCenter, territoryId } from './20_budget';

const FEE = DEMO_CLIENT.fee_pct / 100;
const CHANNEL = { id: 1, name: 'Facebook' };

let leadIndex = null;
function leadStats() {
  if (leadIndex) return leadIndex;
  leadIndex = new Map();
  allLeads().forEach((l) => {
    const k = `${l.date_created.slice(0, 10)}|${l.vertical}|${l.territory}|${l.zip_code}`;
    if (!leadIndex.has(k)) leadIndex.set(k, []);
    leadIndex.get(k).push(l);
  });
  return leadIndex;
}

function leadsFor(rows) {
  const keys = new Set(rows.map((r) => `${r.date}|${r.vertical}|${r.market}`));
  const out = [];
  leadStats().forEach((list, k) => {
    const prefix = k.split('|').slice(0, 3).join('|');
    if (keys.has(prefix)) out.push(...list);
  });
  return out;
}

function metricsFromLeads(leads, spend) {
  const m = {
    total_spend_sum: round2(spend),
    total_leads_sum: leads.length,
    self_books_sum: 0,
    accepted_sum: 0,
    success_sum: 0,
    out_of_areas_count_sum: 0,
    booked_sum: 0,
    scheduled_sum: 0,
    issues_sum: 0,
    demos_sum: 0,
    gross_sales_sum: 0,
    net_sales_sum: 0,
    net_amount_sum: 0,
  };
  leads.forEach((l) => {
    if (l.success_self_book) m.self_books_sum += 1;
    if (l.delivery_status === 'delivered') { m.accepted_sum += 1; m.success_sum += 1; }
    if (l.decline_reason === 'out_of_area') m.out_of_areas_count_sum += 1;
    if (l._appt) { m.booked_sum += 1; m.scheduled_sum += 1; }
    if (l._issued) m.issues_sum += 1;
    if (l._demoed) m.demos_sum += 1;
    if (l._sold) { m.gross_sales_sum += 1; m.net_sales_sum += 1; m.net_amount_sum += l._net; }
  });
  m.net_amount_sum = round2(m.net_amount_sum);
  m.gross_sold_sum = m.gross_sales_sum;
  m.net_sold_sum = m.net_sales_sum;
  m.client_id = DEMO_CLIENT.id;
  return m;
}

function metrics(rows) {
  return metricsFromLeads(leadsFor(rows), rows.reduce((s, r) => s + r.spend, 0));
}

function scoped(params) {
  const after = params.get('date_after');
  const before = params.get('date_before');
  return dailyRows().filter((r) => (!after || r.date >= after) && (!before || r.date <= before));
}

function byDate(params) {
  const groups = groupBy(scoped(params), (r) => r.date);
  return [...groups.entries()].sort().map(([date, rows]) => ({ date, ...metrics(rows) }));
}

function grouped(params, keyFn, labelFn) {
  const groups = groupBy(scoped(params), keyFn);
  return [...groups.entries()].map(([k, rows]) => ({ ...labelFn(k, rows[0]), ...metrics(rows) }));
}

function paged(rows, params, path) {
  const page = Number(params.get('page') || 1);
  const size = Number(params.get('page_size') || 25);
  const sorted = [...rows].sort((a, b) => b.total_leads_sum - a.total_leads_sum);
  return { count: sorted.length, next: nextUrl(path, params, page, size, sorted.length), previous: null, results: sorted.slice((page - 1) * size, page * size) };
}

// ZIP-level breakdowns come from the leads (spend isn't split below market).
function geoRows(params, keyFn, labelFn) {
  const after = params.get('date_after');
  const before = params.get('date_before');
  const search = (params.get('search') || '').toLowerCase();
  const leads = allLeads().filter((l) => {
    const d = l.date_created.slice(0, 10);
    return (!after || d >= after) && (!before || d <= before);
  });
  const groups = groupBy(leads, keyFn);
  return [...groups.entries()]
    .map(([k, list]) => ({ ...labelFn(k, list[0]), ...metricsFromLeads(list, 0) }))
    .filter((r) => !search || JSON.stringify(r).toLowerCase().includes(search));
}

// --- pacing (current month) -------------------------------------------------

function pacingContext(params) {
  const t = today();
  const year = Number(params.get('year') || t.getFullYear());
  const month = Number(params.get('month') || t.getMonth() + 1);
  const first = new Date(year, month - 1, 1);
  const key = monthKey(first);
  const daysInMonth = new Date(year, month, 0).getDate();
  const isCurrent = key === monthKey(t);
  const elapsed = isCurrent ? Math.max(1, t.getDate() - 1) : daysInMonth;
  const rows = dailyRows().filter((r) => r.date.startsWith(key));
  return { first, key, daysInMonth, elapsed, rows, isCurrent };
}

function segmentBudgets(first) {
  return budgetCenter(new URLSearchParams({ period: iso(first) })).segments;
}

function pacingRow({ v, m, budget, ctx }) {
  const rows = ctx.rows.filter((r) => (!v || r.vertical === v.name) && (!m || r.market === m.name));
  const mtd = rows.reduce((s, r) => s + r.spend, 0);
  const yest = rows.filter((r) => r.date === iso(addDays(today(), -1))).reduce((s, r) => s + r.spend, 0);
  const projected = (mtd / ctx.elapsed) * ctx.daysInMonth;
  const remainingDays = Math.max(1, ctx.daysInMonth - ctx.elapsed);
  const last30 = dailyRows().filter((r) => r.date >= iso(addDays(today(), -30)) && (!v || r.vertical === v.name) && (!m || r.market === m.name));
  const last30Spend = last30.reduce((s, r) => s + r.spend, 0);
  const last30Rev = metrics(last30).net_amount_sum;
  return {
    budget_amount: round2(budget),
    adjustment_total: 0,
    month_to_date_spend: round2(mtd),
    yesterday_spend: round2(yest),
    projected_spend: round2(projected),
    budget_remaining: round2(Math.max(0, budget - mtd)),
    remaining_per_day: round2(Math.max(0, budget - mtd) / remainingDays),
    pacing_percentage: budget ? Math.round((projected / budget) * 1000) / 10 : null,
    last_30_spend: round2(last30Spend),
    last_30_net_revenue: round2(last30Rev),
    last_30_cost_of_marketing: last30Rev ? (last30Spend * (1 + FEE)) / last30Rev : null,
    workflow_status: 'active',
    is_turned_off: false,
    has_pending_turn_off: false,
    starts_on: iso(ctx.first),
    spend_data_through: iso(addDays(today(), -1)),
    client_id: DEMO_CLIENT.id,
    client_name: DEMO_CLIENT.name,
    channel_name: CHANNEL.name,
    channel_id: CHANNEL.id,
  };
}

async function pacingSegments(params) {
  const ctx = pacingContext(params);
  const segs = await segmentBudgets(ctx.first);
  return segs.map((s) => {
    const v = VERTICALS.find((x) => x.id === s.vertical);
    const m = MARKETS.find((x) => territoryId(x) === s.location);
    const row = pacingRow({ v, m, budget: Number(s.effective_amount), ctx });
    return {
      ...row,
      adjustment_total: Number(s.adjustment_total),
      segment_key: `${v.id}-${territoryId(m)}-${CHANNEL.id}`,
      vertical_names: [v.name],
      vertical_name: v.name,
      vertical_id: v.id,
      location_names: [m.name],
      location_name: m.name,
      location_id: territoryId(m),
    };
  });
}

async function pacingVerticals(params) {
  const segs = await pacingSegments(params);
  const ctx = pacingContext(params);
  return VERTICALS.map((v) => {
    const budget = segs.filter((s) => s.vertical_id === v.id).reduce((a, s) => a + s.budget_amount, 0);
    return { ...pacingRow({ v, budget, ctx }), vertical_name: v.name, vertical_id: v.id };
  });
}

async function pacingOverall(params) {
  const ctx = pacingContext(params);
  const segs = await pacingSegments(params);
  const budget = segs.reduce((a, s) => a + s.budget_amount, 0);
  const base = pacingRow({ budget, ctx });
  const withFee = (n) => round2(n * (1 + FEE));
  const remainingDays = Math.max(1, ctx.daysInMonth - ctx.elapsed);
  const verticals = await pacingVerticals(params);
  return [{
    client_id: DEMO_CLIENT.id,
    client_name: DEMO_CLIENT.name,
    budget_amount: withFee(budget),
    month_to_date_spend: withFee(base.month_to_date_spend),
    projected_spend: withFee(base.projected_spend),
    yesterday_spend: withFee(base.yesterday_spend),
    budget_remaining: withFee(base.budget_remaining),
    remaining_per_day: withFee(base.remaining_per_day),
    calendar_budget_remaining_per_day: withFee(base.budget_remaining / remainingDays),
    pacing_percentage: base.pacing_percentage,
    vertical_budget_breakdown: verticals.map((v) => ({
      vertical: v.vertical_name, budget_amount: v.budget_amount, month_to_date_spend: v.month_to_date_spend, projected_spend: v.projected_spend,
    })),
  }];
}

async function pacingBudgets(params) {
  const ctx = pacingContext(params);
  const segs = await pacingSegments(params);
  return segs.map((s, i) => ({
    id: 990100 + i,
    client: DEMO_CLIENT.id,
    client_data: { name: DEMO_CLIENT.name },
    amount: s.budget_amount,
    effective_amount: s.budget_amount,
    adjustment_total: s.adjustment_total,
    projected_spend: s.projected_spend,
    month_to_date_spend: s.month_to_date_spend,
    budget_remaining: s.budget_remaining,
    remaining_per_day: s.remaining_per_day,
    yesterday_spend: s.yesterday_spend,
    yesterday_spend_with_fees: round2(s.yesterday_spend * (1 + FEE)),
    channel: CHANNEL.id,
    channel_name: CHANNEL.name,
    verticals_data: [{ vertical: s.vertical_name }],
    locations_data: [{ name: s.location_name }],
    pacing_metadata: { days_remaining: ctx.daysInMonth - ctx.elapsed },
  }));
}

// --- peer benchmark ---------------------------------------------------------

// Peer medians per vertical; the demo client sits a bit above the median.
const PEERS = {
  Bath: { set: 0.38, issue: 0.72, close: 0.22, ticket: 13200 },
  Windows: { set: 0.35, issue: 0.69, close: 0.20, ticket: 12800 },
  Roofing: { set: 0.33, issue: 0.70, close: 0.23, ticket: 16100 },
};

function cohort(params) {
  const wanted = (params.get('verticals') || params.get('vertical') || VERTICALS.map((v) => v.name).join(','))
    .split(',').map((s) => s.trim().toLowerCase());
  return VERTICALS.filter((v) => wanted.includes(v.name.toLowerCase())).map((v) => {
    const p = PEERS[v.name];
    const band = (x) => ({ p25: x * 0.82, p50: x, p75: x * 1.15, p90: x * 1.3 });
    const rates = {
      set_rate: p.set, issue_rate: p.issue, demo_rate: 0.86, net_close_rate: p.close / 0.86,
      lead_to_booked_rate: p.set, lead_to_issue_rate: p.set * p.issue, lead_to_demo_rate: p.set * p.issue * 0.86,
      lead_to_sale_rate: p.set * p.issue * p.close, avg_ticket: p.ticket, revenue_per_lead: p.set * p.issue * p.close * p.ticket,
      self_book_rate: 0.14, cost_of_marketing: 0.16,
    };
    const row = { vertical: v.name, client_count: 38, reporting_client_count: 31 };
    Object.entries(rates).forEach(([k, x]) => {
      row[k] = x;
      row[`${k}_n`] = 31;
      Object.entries(band(x)).forEach(([b, val]) => { row[`${k}_${b}`] = val; });
      row[`${k}_client_percentile`] = 62 + (k.length % 20);
    });
    row.market_context = { cpl_change_pct: 0.06, com_change_pct: -0.02, close_rate_change_pct: 0.01, avg_ticket_change_pct: 0.03, lead_volume_change_pct: 0.08 };
    return row;
  });
}

function cohortTrends(params) {
  const v = params.get('vertical') || 'Bath';
  const p = PEERS[v] || PEERS.Bath;
  const series = (x, lift) => Array.from({ length: 6 }, (_, i) => x * (1 + lift * (i / 5)) * (0.97 + ((i * 7) % 5) / 100));
  const mk = (lift) => ({
    set_rate: series(p.set, lift), issue_rate: series(p.issue, lift / 2), demo_rate: series(0.86, lift / 3), net_close_rate: series(p.close / 0.86, lift),
  });
  return { client: mk(0.12), vertical_avg: mk(0) };
}

const NARRATIVE = 'Your set rate is running ahead of similar bath remodelers in your markets, '
  + 'and close rate is in line with the group. The biggest lever left is speed-to-lead on '
  + 'evening and weekend leads, which book at a lower rate than weekday daytime leads.';

export const routes = [
  ['GET', 'dashboard/client-summary/client-date', ({ params }) => byDate(params)],
  ['GET', 'dashboard/client-budgets/pacing', ({ params }) => pacingBudgets(params)],
  ['GET', 'dashboard/client-budgets/pacing-overall', ({ params }) => pacingOverall(params)],
  ['GET', 'dashboard/client-budgets/pacing-segments', ({ params }) => pacingSegments(params)],
  ['GET', 'dashboard/client-budgets/pacing-verticals', ({ params }) => pacingVerticals(params)],
  ['GET', 'dashboard/daily-pulse/client', () => [{
    client_id: DEMO_CLIENT.id,
    ingestion_health: { has_mapping: true, tasks: [{ name: 'backend_data', status: 'succeeded', fresh: true, finished_at: `${iso(today())}T06:12:00Z`, runtime_seconds: 41, max_created_date: iso(addDays(today(), -1)) }] },
  }]],
  ['GET', 'dashboard/channel-summary/channel-date', ({ params }) => grouped(params, () => CHANNEL.name, () => ({ uuid: 'demo-fb', channel__name: CHANNEL.name, channel: CHANNEL.id, channel_id: CHANNEL.id }))],
  ['GET', 'dashboard/daily-pulse/state', ({ params }) => grouped(params, () => 'NC', () => ({ state: 'NC', state_name: 'North Carolina' }))],
  ['GET', 'dashboard/daily-pulse/dma', ({ params }) => grouped(params, (r) => r.market, (k) => {
    const m = MARKETS.find((x) => x.name === k);
    return { dma__name: m.dma, dma_name: m.dma, dma__id: m.id, dma_id: m.id };
  })],
  ['GET', 'dashboard/daily-pulse/territory-campaign', ({ params }) => grouped(params, (r) => `${r.market}|${r.vertical}`, (k) => {
    const [mName, vName] = k.split('|');
    const v = VERTICALS.find((x) => x.name === vName);
    return { client_territory__name: mName, territory_name: mName, vertical: vName, vertical_fk: v.id, vertical_fk_id: v.id };
  })],
  ['GET', 'dashboard/daily-pulse/vertical-cohort-narrative', () => ({ narrative: NARRATIVE })],
  ['GET', 'dashboard/daily-pulse/vertical-cohort-trends', ({ params }) => cohortTrends(params)],
  ['GET', 'dashboard/daily-pulse/vertical-cohort', ({ params }) => cohort(params)],
  ['GET', 'dashboard/daily-pulse/vertical', ({ params }) => grouped(params, (r) => r.vertical, (k) => ({ vertical: k }))],
  ['GET', 'dashboard/daily-pulse/city', ({ params, path }) => paged(geoRows(params, (l) => l.city, (k, l) => ({
    city_name: k, city: k, state_name: 'NC', state: 'NC', territory_names: l.territory,
  })), params, path)],
  ['GET', 'dashboard/daily-pulse/county', ({ params, path }) => paged(geoRows(params, (l) => ZIP_FACTS[l.zip_code]?.county || '', (k) => ({
    county_name: k, county: k, state_name: 'NC', state: 'NC',
  })), params, path)],
  ['GET', 'dashboard/daily-pulse/zip', ({ params, path }) => paged(geoRows(params, (l) => l.zip_code, (k) => ({
    zip_code_value: k, zip_code: k, city: ZIP_FACTS[k]?.city || '', state_name: 'NC', state: 'NC',
    population: ZIP_FACTS[k]?.population || 0, household_income: 68000 + (Number(k) % 37) * 1500,
  })), params, path)],
  ['POST', 'dashboard/daily-pulse/card-insight', () => ({ insight: null }), { silent: true }],
  ['GET', 'dashboard/client-processed-csv/latest', () => ({})],
  ['GET', 'dashboard/backend-data', ({ params }) => {
    const field = params.get('date_field') || 'sold_date';
    const after = params.get('date_after');
    const before = params.get('date_before');
    const vertical = params.get('vertical');
    const rows = allLeads().filter((l) => l._appt && (!vertical || l.vertical === vertical)).map((l) => ({
      id: l.id,
      unique_id: `SR-${l.id}`,
      created_date: l.date_created.slice(0, 10),
      appointment_date: l.date_created.slice(0, 10),
      sold_date: l._sold_date,
      phone: l.phone,
      email: l.email,
      issue: l._issued,
      demo: l._demoed,
      gross_amount: l._net ? round2(l._net * 1.08) : null,
      net_amount: l._net,
      status: l._sold ? 'Sold' : l._issued ? 'Demo – No Sale' : 'Appointment Set',
      vertical: l.vertical,
      zip_code: l.zip_code,
      territory_name: l.territory,
      channel_name: CHANNEL.name,
      state: 'NC',
    })).filter((r) => r[field] && (!after || r[field] >= after) && (!before || r[field] <= before))
      .sort((a, b) => (a[field] < b[field] ? 1 : -1));
    const page = Number(params.get('page') || 1);
    const size = Number(params.get('page_size') || 50);
    return { count: rows.length, next: nextUrl('dashboard/backend-data', params, page, size, rows.length), previous: null, results: rows.slice((page - 1) * size, page * size) };
  }],
];
